import React, { useCallback, useEffect, useState } from 'react';
import { saveAs } from 'file-saver';
import { getApiWithToken, getDownloadApiWithToken, putApiWithToken } from '../../utils/ApiInterfaces';
import { useUserContext } from '../../context/userContext';
import Spinwheel from '../../components/common/Spinwheel';
import '../DPR/DLR/DLRReport.css';
import './ConcretePourCards.css';

const base = '/concrete-pour-cards';
const date = value => value ? new Date(value).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—';
export default function ConcretePourReports() {
  const { userDetails } = useUserContext();
  const inspector = (userDetails?.roleId?.type || userDetails?.roleId?.name) === 'inspector';
  const [data, setData] = useState({ reports: [], labels: {}, capabilities: {} });
  const [range, setRange] = useState({ from: '', to: '' });
  const [query, setQuery] = useState({ from: '', to: '', page: 1 });
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [listLoading, setListLoading] = useState(true);
  const t = data.labels;
  const load = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getApiWithToken(`${base}/reports?${new URLSearchParams(query)}`);
      setData(response.data.data);
    } finally { setListLoading(false); }
  }, [query]);
  const perform = useCallback(async action => {
    setBusy(true); setError('');
    try { await action(); } catch (e) { setError(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { perform(load); }, [load, perform]);
  const file = (report, view) => {
    const previewWindow = view ? window.open('', '_blank') : null;
    if (previewWindow) previewWindow.opener = null;
    return perform(async () => {
      try {
        const response = await getDownloadApiWithToken(`${base}/${report.cardId}/file`);
        if (view) {
          const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
          if (previewWindow) previewWindow.location.href = url;
          else window.open(url, '_blank', 'noopener,noreferrer');
          window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } else saveAs(response.data, report.fileName);
      } catch (e) {
        if (previewWindow) previewWindow.close();
        throw e;
      }
    });
  };
  const share = (report, action) => perform(async () => {
    await putApiWithToken(`${base}/${report.cardId}/${action}`, {}); await load();
  });
  return <div className="main-content"><div className="container-fluid">
    <h3 className="font-blue">{t.reportListing}</h3><p>{t.reportHelp}</p>
    <form className="d-flex flex-wrap gap-3 mb-4" onSubmit={e => { e.preventDefault(); setQuery({ ...range, page: 1 }); }}>
      <label>{t.from}<input className="form-control" type="date" value={range.from} max={range.to || undefined} onChange={e => setRange({ ...range, from: e.target.value })} /></label>
      <label>{t.to}<input className="form-control" type="date" value={range.to} min={range.from || undefined} onChange={e => setRange({ ...range, to: e.target.value })} /></label>
      <button className="btn btn-primary align-self-end" disabled={busy}>{t.filter}</button>
      <button type="button" className="btn btn-outline-secondary align-self-end" disabled={busy} onClick={() => { setRange({ from: '', to: '' }); setQuery({ from: '', to: '', page: 1 }); }}>{t.reset}</button>
    </form>
    {error && <p role="alert" className="alert alert-danger">{error}</p>}
    {listLoading ? <div className="periodic-dlr-state"><Spinwheel /></div> : <>
    {busy && <p role="status">{t.loading}</p>}
    <div className="table-responsive"><table className="table table-bordered"><thead><tr>
      {[t.pourCardNo, t.date, t.location, t.estimatedVolume, t.gradeShort || t.grade, t.actions].map((label, i) => <th key={i}>{label}</th>)}
    </tr></thead><tbody>{data.reports.map(report => <tr key={report._id}>
      <td>{report.pourCardNo}</td><td>{date(report.date)}</td><td>{report.location}</td>
      <td>{report.estimatedVolume ?? '—'}</td><td>{report.grade || '—'}</td>
      <td><div className="d-flex flex-wrap gap-2">
        <button className="btn btn-sm btn-outline-primary" disabled={busy || !data.capabilities.download} onClick={() => file(report, true)}>{t.view}</button>
        <button className="btn btn-sm btn-outline-primary" disabled={busy || !data.capabilities.download} onClick={() => file(report, false)}>{t.download}</button>
        {!inspector && report.status !== 'shared' && data.capabilities.share && <button className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => share(report, 'share')}>{t.sendToInspection}</button>}
        {!inspector && report.status === 'shared' && data.capabilities.unshare && <button className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => share(report, 'unshare')}>{t.unshare}</button>}
      </div></td>
    </tr>)}</tbody></table></div>
    {!busy && !data.reports.length && <p>{t.empty}</p>}
    <div className="d-flex gap-3 align-items-center my-3"><button className="btn btn-outline-secondary" disabled={busy || query.page <= 1} onClick={() => setQuery({ ...query, page: query.page - 1 })}>‹</button><span>{query.page} / {Math.max(1, Math.ceil((data.total || 0) / (data.limit || 25)))}</span><button className="btn btn-outline-secondary" disabled={busy || query.page * (data.limit || 25) >= (data.total || 0)} onClick={() => setQuery({ ...query, page: query.page + 1 })}>›</button></div>
    </>}
  </div></div>;
}
